import { Box, Typography } from "@mui/material";
import { LocalFireDepartment } from "@mui/icons-material";

import styles from "./AllergensMenu.module.scss";

import FoodInfoToolTip from './menuCard/FoodInfoToolTip';
import MenuSection from './menuCard/MenuSection';

// Content panel for new-style allergens (resolved objects with icon, name, description)
function NewAllergensList({ allergens, menuContainerRef }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
      }}
    >
      {allergens.map((allergen) => (
        <Box
          key={allergen._id}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
            minWidth: "48px",
          }}
        >
          <FoodInfoToolTip key={allergen._id} name={allergen.name} description={allergen.description} parentRef={menuContainerRef}>
            <img src={allergen.icon} width="30px" height="30px" alt={allergen.name} />
          </FoodInfoToolTip>
        </Box>
      ))}
    </Box>
  );
}

// Content panel for legacy allergens (text string + optional calories)
function LegacyAllergiesContent({ item }) {
  return (
    <Box className={styles.allergiesContainer}>
      {item?.allergies && (
        <Box className={styles.allergiesContent}>
          <Typography variant="body2" className={styles.allergiesText}>
            {item.allergies}
          </Typography>
        </Box>
      )}

      {item?.calories && (
        <Box className={styles.caloriesContent}>
          <Typography variant="body2" className={styles.caloriesLabel}>
            Calories: <span className={styles.caloriesValue}>{item.calories}</span>
          </Typography>
        </Box>
      )}

      <Box className={styles.allergiesFooter}>
        <Typography variant="caption" className={styles.importantText}>
          <span className={styles.importantLabel}>Important:</span> Check product
          information regularly as recipes may change. Individual items may
          vary. We try to remove bones from boneless items, but please check
          carefully.
        </Typography>
      </Box>
    </Box>
  );
}

// resolvedAllergens: array of allergen objects {_id, name, icon, description} (new style)
// item: menu item object used for legacy fallback (item.allergies, item.calories)
const AllergensMenu = ({ item, resolvedAllergens, menuContainerRef }) => {
  const hasAllergens = resolvedAllergens?.length > 0;
  const hasLegacyAllergens = !!item?.allergies;
  const hasCalories = !!item?.calories;

  // New allergens take priority; fall back to legacy; show nothing if neither
  if (!hasAllergens && !hasLegacyAllergens && !hasCalories) return null;

  // Calories-only (legacy, no allergens of either kind): show inline without full section
  if (!hasAllergens && !hasLegacyAllergens && hasCalories) {
    return (
      <Box className={styles.allergensMenuContainer}>
        <Box className={styles.caloriesOnlyContainer}>
          <Box className={styles.buttonContent}>
            <Typography variant="body2" className={styles.buttonText}>
              Calories
            </Typography>
            <LocalFireDepartment className={styles.caloriesIcon} />
          </Box>
          <Typography variant="body2" className={styles.caloriesDirectValue}>
            {item.calories}
          </Typography>
        </Box>
      </Box>
    );
  }

  let title;
  if (hasAllergens) {
    title = "Allergens";
  } else if (hasLegacyAllergens && hasCalories) {
    title = "Allergen & Calorie Information";
  } else if (hasLegacyAllergens) {
    title = "Allergen Information";
  } else {
    title = "Calorie Information";
  }

  const noMenuImage = !item?.images?.length;

  return (
    <MenuSection title={title} noMenuImage={noMenuImage}>
      {hasAllergens ? (
        <NewAllergensList allergens={resolvedAllergens} menuContainerRef={menuContainerRef} />
      ) : (
        <LegacyAllergiesContent item={item} />
      )}
    </MenuSection>
  );
};

export default AllergensMenu;
